import { useMemo } from 'react';

import { type DocumentNode, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { getComparisonDates } from '@/modules/analytics/utils/getComparisonDates';

import { useAiAgentsVariables } from './useAiAgentsVariables';
import { useComparisonMode } from '../../../hooks/useComparisonMode';

export type SentimentCategory = 'positive' | 'neutral' | 'negative';
export type GoalCategory = 'achieved' | 'partially' | 'not_achieved';

export type DistributionResult<T extends string> = Record<T, number> & { totalCount: number };

interface DistributionRow<T extends string> {
  category: T;
  conversations: number | null;
  percentage: number | null;
  total_count: number | null;
}

const SENTIMENT_CATEGORIES: readonly SentimentCategory[] = ['positive', 'neutral', 'negative'];
const GOAL_CATEGORIES: readonly GoalCategory[] = ['achieved', 'partially', 'not_achieved'];
const SENTIMENT_CATEGORY_SET: ReadonlySet<string> = new Set(SENTIMENT_CATEGORIES);
const GOAL_CATEGORY_SET: ReadonlySet<string> = new Set(GOAL_CATEGORIES);

// Distinct operation names so Apollo's cache treats current and comparison
// queries as separate (same shape, different time window) — mirrors the
// pattern in `useAiAgentsInsights`.
const buildDistributionQuery = (rpcName: string, opName: string): DocumentNode => gql`
  query ${opName}(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ${rpcName}(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      category
      conversations
      percentage
      total_count
    }
  }
`;

const SENTIMENT_QUERY = buildDistributionQuery(
  'ai_agents_sentiment_distribution',
  'AiAgentsSentimentDistribution'
);
const SENTIMENT_QUERY_COMPARISON = buildDistributionQuery(
  'ai_agents_sentiment_distribution',
  'AiAgentsSentimentDistributionComparison'
);
const GOAL_QUERY = buildDistributionQuery(
  'ai_agents_goal_distribution',
  'AiAgentsGoalDistribution'
);
const GOAL_QUERY_COMPARISON = buildDistributionQuery(
  'ai_agents_goal_distribution',
  'AiAgentsGoalDistributionComparison'
);

const toResult = <T extends string>(
  rows: DistributionRow<T>[] | undefined,
  categories: readonly T[],
  allowed: ReadonlySet<string>
): DistributionResult<T> => {
  const result = { totalCount: 0 } as DistributionResult<T>;
  const buckets = result as Record<T, number>;
  for (const category of categories) {
    buckets[category] = 0;
  }
  if (!rows) {
    return result;
  }

  for (const row of rows) {
    if (row.total_count != null) {
      result.totalCount = row.total_count;
    }
    if (allowed.has(row.category)) {
      buckets[row.category] = row.percentage ?? 0;
    }
  }

  return result;
};

const useDistribution = <T extends string>(
  query: DocumentNode,
  comparisonQuery: DocumentNode,
  categories: readonly T[],
  allowed: ReadonlySet<string>
) => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const { variables: currentVariables, skip } = useAiAgentsVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useAiAgentsVariables(comparisonDates);

  const currentResult = useQuery<{ rows: DistributionRow<T>[] }>(query, {
    variables: currentVariables,
    skip,
  });
  const comparisonResult = useQuery<{ rows: DistributionRow<T>[] }>(comparisonQuery, {
    variables: comparisonVariables,
    skip: skip || !isComparing,
  });

  const current = useMemo(
    () => toResult(currentResult.data?.rows, categories, allowed),
    [currentResult.data?.rows, categories, allowed]
  );
  const previous = useMemo(
    () => toResult(comparisonResult.data?.rows, categories, allowed),
    [comparisonResult.data?.rows, categories, allowed]
  );

  return { current, previous };
};

export const useSentimentDistribution = () =>
  useDistribution(
    SENTIMENT_QUERY,
    SENTIMENT_QUERY_COMPARISON,
    SENTIMENT_CATEGORIES,
    SENTIMENT_CATEGORY_SET
  );

export const useGoalDistribution = () =>
  useDistribution(GOAL_QUERY, GOAL_QUERY_COMPARISON, GOAL_CATEGORIES, GOAL_CATEGORY_SET);
